import { FEATURE_NAMES } from "@/loggers/types";
import { useUserStore } from "@/store/user/user";

export const getScvCode = (userStore: ReturnType<typeof useUserStore>) => userStore.customerCode || "N/A";

export const getUserType = (userStore: ReturnType<typeof useUserStore>) => {
  if (userStore.isUserInternal) {
    return "Internal User";
  } else {
    return userStore.customerCode ? "External User" : "N/A";
  }
};

const buildLogContext = () => {
  const userStore = useUserStore();
  const scvCode = getScvCode(userStore);
  const userType = getUserType(userStore);
  return `${scvCode} | ${userType}`;
};

const getRumApi = () => globalThis.MOP_RUM?.api;

const isMopRumAvailable = (method: "pushError" | "pushLog" | "pushMeasurement" | "pushEvent"): boolean => {
  if (!window.MOP_RUM?.api?.[method]) {
    return false;
  }
  return true;
};

const handleMopRumError = (e: unknown, logType: "error" | "info") => {
  console.error(`MOP_RUM: Failed to log ${logType}:`, e);
};

const handleMopRumMetricError = (e: unknown, type: "event" | "measurement") => {
  console.error(`MOP_RUM: Failed to publish metric ${type}:`, e);
};

export const logError = (feature: FEATURE_NAMES, error: string) => {
  if (!isMopRumAvailable("pushError")) return;

  try {
    const err = new Error(`[${feature}] | ${buildLogContext()} : ${error}`);
    window.MOP_RUM.api.pushError(err);
  } catch (e) {
    handleMopRumError(e, "error");
  }
};

export const logInfo = (feature: FEATURE_NAMES, info: string) => {
  if (!isMopRumAvailable("pushLog")) return;

  try {
    window.MOP_RUM.api.pushLog([`[${feature}] | ${buildLogContext()} : ${info}`]);
  } catch (e) {
    handleMopRumError(e, "info");
  }
};

/* ---------------- EVENTS ---------------- */

export const logEvent = (feature: FEATURE_NAMES, eventName: string, attributes?: Record<string, string | number>) => {
  if (!isMopRumAvailable("pushEvent")) return;
  try {
    const userStore = useUserStore();
    const userType = getUserType(userStore);
    const scvCode = getScvCode(userStore);
    const eventAttributes = { ...attributes };
    if (attributes?.statusCode !== undefined && attributes?.statusCode !== null) {
      eventAttributes.statusCode = attributes.statusCode.toString();
    }
    getRumApi()?.pushEvent(`${eventName} | ${feature} | ${userType} | ${scvCode}`, {
      ...attributes,
      statusCode: attributes?.statusCode?.toString() || "unknown"
    });
  } catch (e) {
    handleMopRumMetricError(e, "event");
  }
};

/* ---------------- MEASUREMENTS ---------------- */

export const logMeasurement = (
  feature: FEATURE_NAMES,
  metricName: string,
  value: number,
  attributes?: Record<string, string | number>
) => {
  if (!isMopRumAvailable("pushMeasurement")) return;
  try {
    getRumApi()?.pushMeasurement({
      type: `${metricName} | ${feature} | ${attributes.api} | ${attributes.params}`,
      values: {
        metric_value: value
      }
    });
  } catch (e) {
    handleMopRumMetricError(e, "measurement");
  }
};
